import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useConsent } from "@/lib/ConsentContext";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";

export default function ConsentBanner() {
  const { prefs, acceptAll, rejectAll, set } = useConsent();
  const { t } = useTranslation();
  const { lang } = useLocale();
  const { pathname } = useLocation();
  const [manage, setManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);      // NOT pre-selected
  const [advertising, setAdvertising] = useState(false);   // NOT pre-selected
  const [openedFromSettings, setOpened] = useState(false);

  useEffect(() => {
    const listener = () => setOpened(true);
    window.addEventListener("djspy-open-consent", listener);
    return () => window.removeEventListener("djspy-open-consent", listener);
  }, []);

  // Hide on /admin/* routes — admins have already signed in and the banner
  // overlaps modal footers on the dashboard.
  if (pathname.startsWith("/admin")) return null;

  const hidden = prefs && !openedFromSettings;
  if (hidden) return null;

  const savePrefs = () => {
    set({ analytics, advertising, reason: "custom" });
    setOpened(false);
    setManage(false);
  };

  return (
    <div
      data-testid="consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label={t("consent.dialogLabel")}
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 z-[9997] hairline rounded-2xl bg-black/95 backdrop-blur-xl p-5 md:p-6 max-w-3xl md:mx-auto shadow-2xl border-white/15"
    >
      {!manage ? (
        <>
          <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand">{t("consent.kicker")}</div>
          <p className="text-white/85 mt-2 text-sm leading-relaxed">
            <Trans
              i18nKey="consent.body"
              components={{
                1: <Link to={localisedTo("/privacy", lang)} className="underline text-red-brand" />,
              }}
            />
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="consent-accept" onClick={() => { acceptAll(); setOpened(false); setManage(false); }} className="btn-primary !py-2 !px-4 text-sm">{t("consent.acceptAll")}</button>
            <button data-testid="consent-reject" onClick={() => { rejectAll(); setOpened(false); }} className="btn-primary !py-2 !px-4 text-sm !bg-white/10 hover:!bg-white/15">{t("consent.rejectNonEssential")}</button>
            <button data-testid="consent-manage" onClick={() => setManage(true)} className="btn-ghost !py-2 !px-4 text-sm">{t("consent.managePreferences")}</button>
          </div>
        </>
      ) : (
        <>
          <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand">{t("consent.manageKicker")}</div>
          <div className="mt-4 space-y-3">
            <label className="flex items-start gap-3">
              <input type="checkbox" checked disabled className="!w-4 !h-4 mt-1" />
              <span>
                <b className="text-white">{t("consent.necessaryTitle")}</b>
                <span className="block text-white/60 text-xs">{t("consent.necessaryBody")}</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input data-testid="consent-analytics" type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="!w-4 !h-4 mt-1" />
              <span>
                <b className="text-white">{t("consent.analyticsTitle")}</b>
                <span className="block text-white/60 text-xs">{t("consent.analyticsBody")}</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input data-testid="consent-advertising" type="checkbox" checked={advertising} onChange={(e) => setAdvertising(e.target.checked)} className="!w-4 !h-4 mt-1" />
              <span>
                <b className="text-white">{t("consent.advertisingTitle")}</b>
                <span className="block text-white/60 text-xs">{t("consent.advertisingBody")}</span>
              </span>
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="consent-save" onClick={savePrefs} className="btn-primary !py-2 !px-4 text-sm">{t("consent.savePreferences")}</button>
            <button data-testid="consent-reject-2" onClick={() => { rejectAll(); setOpened(false); setManage(false); }} className="btn-primary !py-2 !px-4 text-sm !bg-white/10 hover:!bg-white/15">{t("consent.rejectNonEssential")}</button>
          </div>
        </>
      )}
    </div>
  );
}
